import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '../../../cdl/Button/Button';
import { DropdownMenu } from '../../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { ButtonGroup } from '../../../common/buttons/ButtonGroup';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconCancel } from '../../../common/icons/cdl/Cancel';
import { IconCopy } from '../../../common/icons/cdl/Copy';
import { IconDots } from '../../../common/icons/cdl/Dots';
import { IconDownload } from '../../../common/icons/cdl/Download';
import { IconListSearch } from '../../../common/icons/cdl/ListSearch';
import { IconPencil } from '../../../common/icons/cdl/Pencil';
import { IconRepeat } from '../../../common/icons/cdl/Repeat';
import { ShortcutMenuContextAction } from '../../../common/ShortcutMenu/components/ShortcutMenuContext';
import { useRegisterShortcutMenuContextActions } from '../../../common/ShortcutMenu/hooks/useRegisterShortcutMenuContextActions';
import { useOrderExport } from '../../../offer/detail/common/hooks/useOrderExport';
import { DeleteDraftConfirmPopup } from '../../common/DeleteDraftConfirmPopup';
import { useOrderDelete } from '../../common/hooks/useOrderDelete';
import { toUpdateOrderRequest } from '../../model/order.mapper';
import { OrderModel, OrderState } from '../../model/OrderModel';
import { OrderTabEnum } from '../../OrderOverview/orderTabsHelper';
import { useOrderRedispatch } from '../common/useOrderRedispatch';
import { OrderCancelModal } from '../components/OrderCancelModal';
import { useOrderUpdate } from '../hooks/useOrderUpdate';

interface OfferCompareActionBarProps {
    order: OrderModel;
}

interface OrderDetailAction {
    id: string;
    label: string;
    onSelect: () => void;
    Icon?: ShortcutMenuContextAction['Icon'];
    destructive?: boolean;
    includeInDropdown?: boolean;
}

export const OfferCompareActionBar = ({ order }: OfferCompareActionBarProps) => {
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
    const updateOrderMutation = useOrderUpdate();
    const deleteOrderMutation = useOrderDelete();
    const { downloadPdf } = useOrderExport();
    const { addLoadingToast, addToast, addErrorToast } = useToasts();
    const role = useRole();
    const navigate = useNavigate();
    const redispatchOrderMutation = useOrderRedispatch();

    const onCancelPopupSubmit = async (cancelReason: string) => {
        await updateOrderMutation.mutateAsync({
            order: {
                ...toUpdateOrderRequest(order),
                state: OrderState.CANCELED,
                cancelReason,
            },
            orderId: order.id,
        });

        setShowCancelModal(false);
    };

    const onDeleteDraftConfirm = () => {
        deleteOrderMutation.mutate(order.id, {
            onSuccess: () => {
                setShowDeleteConfirmPopup(false);
                navigate({ to: '/orders', search: { tab: OrderTabEnum.DRAFTED } });
            },
        });
    };

    const actions: OrderDetailAction[] = [];

    if (role.isCustomer() && role.hasWriteRights(order.customerId)) {
        actions.push({
            id: 'copy-order',
            label: translate('order.copy'),
            Icon: IconCopy,
            includeInDropdown: true,
            onSelect: () =>
                navigate({
                    to: '/orders/new',
                    state: {
                        order,
                    },
                }),
        });
    }

    actions.push({
        id: 'export-order',
        label: translate('order.detail.header.actions.export'),
        Icon: IconDownload,
        includeInDropdown: true,
        onSelect: () =>
            addLoadingToast(
                () => downloadPdf(order.id),
                translate('offer.detail.header.actions.export.downloadingPdf'),
                translate('offer.detail.header.actions.export.success'),
                translate('global.genericToastError')
            ),
    });

    if (role.isCustomer() && !order.isFinished() && !order.isDraft() && role.hasWriteRights(order.customerId)) {
        actions.push({
            id: 'cancel-order',
            label: translate('order.detail.btncancel'),
            Icon: IconCancel,
            includeInDropdown: true,
            onSelect: () => setShowCancelModal(true),
            destructive: true,
        });
    }

    if (role.isSuperAdmin() && order.state !== OrderState.DRAFT) {
        actions.push({
            id: 'show-offer-list',
            label: translate('order.detail.header.actions.showOfferList'),
            Icon: IconListSearch,
            onSelect: () => navigate({ to: '/offers', search: { searchQuery: order.id } }),
        });
    }

    if (role.isSuperAdmin() && order.isDraft()) {
        actions.push({
            id: 'delete-draft',
            label: translate('actionListLabel.delete'),
            includeInDropdown: true,
            onSelect: () => setShowDeleteConfirmPopup(true),
            destructive: true,
        });
    }

    if (role.isSuperAdmin() && order.isOpen()) {
        actions.push({
            id: 'redispatch-order',
            label: translate('order.detail.header.actions.redispatch'),
            Icon: IconRepeat,
            includeInDropdown: true,
            onSelect: () => {
                redispatchOrderMutation.mutate(order.id, {
                    onSuccess: () => {
                        addToast(translate('order.detail.redispatchSuccessToast'));
                    },
                    onError: () => {
                        addErrorToast(translate('order.detail.redispatchErrorToast'));
                    },
                });
            },
        });
    }

    useRegisterShortcutMenuContextActions(
        actions.map((action) => ({
            id: action.id,
            label: action.label,
            onSelect: action.onSelect,
            Icon: action.Icon,
            destructive: action.destructive,
        }))
    );

    return (
        <>
            <ButtonGroup>
                {!role.isSuperAdmin() &&
                !order.isFinished() &&
                !order.isDraft() &&
                role.hasWriteRights(order.customerId) ? (
                    <Button
                        onClick={() =>
                            navigate({
                                to: '/order/edit/$id',
                                params: {
                                    id: order.id,
                                },
                            })
                        }
                        LeadingIcon={IconPencil}
                    >
                        {translate('order.detail.edit')}
                    </Button>
                ) : null}

                {role.isSuperAdmin() && order.state !== OrderState.DRAFT ? (
                    <Button onClick={() => navigate({ to: '/offers', search: { searchQuery: order.id } })}>
                        {translate('order.detail.header.actions.showOfferList')}
                    </Button>
                ) : null}

                <DropdownMenu
                    options={actions
                        .filter((action) => action.includeInDropdown)
                        .map((action) => ({
                            label: action.label,
                            onSelect: action.onSelect,
                            destructive: action.destructive,
                        }))}
                    align="end"
                    trigger={<IconButton Icon={IconDots} emphasis="medium" />}
                />
            </ButtonGroup>

            {showCancelModal ? (
                <OrderCancelModal
                    isOpen={showCancelModal}
                    onSubmit={onCancelPopupSubmit}
                    onDismiss={() => setShowCancelModal(false)}
                    isSubmitting={updateOrderMutation.isPending}
                />
            ) : null}
            <DeleteDraftConfirmPopup
                isOpen={showDeleteConfirmPopup}
                onSubmit={onDeleteDraftConfirm}
                onDismiss={() => setShowDeleteConfirmPopup(false)}
                reference={order.buyerReference}
                isSubmitting={deleteOrderMutation.isPending}
            />
        </>
    );
};
